import React, { useLayoutEffect, useRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import Bottle, { BOTTLE } from './scene/Bottle';
import {
  CAMERA_HOME,
  STUDIO_BACKGROUND,
  StudioEnvironment,
  StudioLights,
  Sweep,
  configureRenderer,
  usePrefersReducedMotion,
} from './scene/studio';

interface HeroBottleProps {
  /** Vertical point the camera looks at; lower values push the bottle up the frame. */
  focusY?: number;
}

const Studio: React.FC<{ reducedMotion: boolean; focusY: number }> = ({ reducedMotion, focusY }) => {
  const bottle = useRef<THREE.Group>(null);
  const camera = useThree((state) => state.camera);

  useLayoutEffect(() => {
    camera.lookAt(0, focusY, 0);
  }, [camera, focusY]);

  useFrame((state) => {
    if (!bottle.current || reducedMotion) return;
    bottle.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.28) * 0.28;
  });

  return (
    <>
      <color attach="background" args={[STUDIO_BACKGROUND]} />
      <StudioEnvironment />
      <StudioLights />
      <Sweep />
      <group ref={bottle}>
        <Bottle />
      </group>
    </>
  );
};

/** Standalone studio shot of the bottle, used by the simpler hero on other pages. */
const HeroBottle: React.FC<HeroBottleProps> = ({ focusY = BOTTLE.centerY - 0.08 }) => {
  const reducedMotion = usePrefersReducedMotion();

  return (
    <Canvas
      className="hero-bottle"
      style={{ position: 'absolute', inset: 0 }}
      camera={{ position: CAMERA_HOME.position.toArray(), fov: CAMERA_HOME.fov, near: 0.1, far: 60 }}
      dpr={[1, 2]}
      shadows="variance"
      frameloop={reducedMotion ? 'demand' : 'always'}
      gl={{ antialias: true, alpha: false, powerPreference: 'high-performance' }}
      onCreated={({ gl }) => configureRenderer(gl)}
    >
      <Studio reducedMotion={reducedMotion} focusY={focusY} />
    </Canvas>
  );
};

export default HeroBottle;
